import axios from 'axios';
import { getCsrfToken, getCurrentCsrfToken, resetCsrfToken } from './csrfService';

/**
 * Сервис для работы с API шеринга
 */
export class ShareService {
  private static baseURL = import.meta.env.VITE_SHARE_SERVER_URL || 'http://localhost:3002';
  private static publicSharesCache: Map<string, { data: any; timestamp: number }> = new Map();

  /**
   * Изменяет флаг публичности для расшаренной ссылки
   * @param shareId - ID расшаренной ссылки
   * @param externalId - Внешний ID анализа (для аутентификации)
   * @param isPublic - Новое значение флага публичности
   * @returns Promise с результатом операции
   */
  static async updatePublicFlag(
    shareId: string, 
    externalId: string, 
    isPublic: boolean
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axios.put(
        `${this.baseURL}/api/share/${shareId}/public`,
        {
          external_id: externalId,
          public: isPublic
        },
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 10000 // 10 секунд таймаут
        }
      );

      return {
        success: true,
        message: response.data.message || 'Флаг публичности успешно изменен'
      };
    } catch (error) {
      console.error('Error updating public flag:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('Шаринг не найден или у вас нет прав на его изменение');
        } else if (error.response?.status === 400) {
          throw new Error(error.response.data.error || 'Некорректные данные запроса');
        } else if (error.response?.status === 500) {
          throw new Error('Внутренняя ошибка сервера');
        } else {
          throw new Error(error.response?.data?.error || 'Ошибка при изменении флага публичности');
        }
      }
      
      throw new Error('Ошибка сети при изменении флага публичности');
    }
  }

  /**
   * Получает информацию о расшаренной ссылке
   * @param shareId - ID расшаренной ссылки
   * @returns Promise с информацией о ссылке
   */
  static async getShareInfo(shareId: string): Promise<{ public: boolean; share_created_at: string }> {
    try {
      const response = await axios.get(
        `${this.baseURL}/api/share/${shareId}`,
        {
          timeout: 10000
        }
      );

      return {
        public: response.data.public || false,
        share_created_at: response.data.share_created_at
      };
    } catch (error) {
      console.error('Error getting share info:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('Шаринг не найден');
        } else if (error.response?.status === 500) {
          throw new Error('Внутренняя ошибка сервера');
        } else {
          throw new Error(error.response?.data?.error || 'Ошибка при получении информации о шаринге');
        }
      }
      
      throw new Error('Ошибка сети при получении информации о шаринге');
    }
  }

  /**
   * Создает расшаренную ссылку для анализа
   * @param externalId - Внешний ID анализа
   * @param items - Элементы анализа для шеринга
   * @returns Promise с результатом создания шеринга
   */
  static async shareAnalysis(
    externalId: string, 
    items: any[]
  ): Promise<{ share_id: string; share_url?: string }> {
    try {
      // Получаем CSRF токен
    let csrfToken = getCurrentCsrfToken();
    if (!csrfToken) {
      console.log('CSRF: Токен не найден, запрашиваем новый');
      csrfToken = await getCsrfToken();
    } else {
      console.log('CSRF: Используем существующий токен из кэша');
    }

      const response = await axios.post(
        `${this.baseURL}/api/share`,
        {
          external_id: externalId,
          items: items
        },
        {
      headers: {
        'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken
          },
          timeout: 10000,
          withCredentials: true // Важно для CSRF токенов
        }
      );

      return {
        share_id: response.data.share_id,
        share_url: response.data.share_url
      };
    } catch (error) {
      console.error('Error sharing analysis:', error);
      
      if (axios.isAxiosError(error)) {
      // Если ошибка CSRF, сбрасываем токен и пробуем еще раз
        if (error.response?.data?.code === 'CSRF_ERROR') {
        resetCsrfToken();
        throw new Error('Ошибка CSRF токена. Попробуйте еще раз.');
      }
      
        if (error.response?.status === 400) {
          throw new Error(error.response.data.error || 'Некорректные данные запроса');
        } else if (error.response?.status === 404) {
          throw new Error('Анализ не найден');
        } else if (error.response?.status === 500) {
          throw new Error('Внутренняя ошибка сервера');
        } else {
          throw new Error(error.response?.data?.error || 'Ошибка при создании шеринга');
        }
      }
      
      throw new Error('Ошибка сети при создании шеринга');
    }
  }

  /**
   * Получает расшаренный анализ по shareId
   * @param shareId - ID расшаренной ссылки
   * @returns Promise с данными анализа
   */
  static async getSharedAnalysis(shareId: string): Promise<any> {
    try {
      const response = await axios.get(
        `${this.baseURL}/api/share/${shareId}`,
        {
          timeout: 10000
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error getting shared analysis:', error);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('Шаринг не найден');
        } else if (error.response?.status === 500) {
          throw new Error('Внутренняя ошибка сервера');
        } else {
          throw new Error(error.response?.data?.error || 'Ошибка при получении расшаренного анализа');
        }
      }

      throw new Error('Ошибка сети при получении расшаренного анализа');
    }
  }

  /**
   * Получает оригинальный анализ (без изменений) по shareId
   * @param shareId - ID расшаренной ссылки
   * @returns Promise с данными оригинального анализа
   */
  static async getOriginalAnalysis(shareId: string): Promise<any> {
    try {
      const response = await axios.get(
        `${this.baseURL}/api/share/${shareId}/original`,
        {
          timeout: 10000
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error getting original analysis:', error);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          throw new Error('Шаринг не найден');
        } else if (error.response?.status === 500) {
          throw new Error('Внутренняя ошибка сервера');
        } else {
          throw new Error(error.response?.data?.error || 'Ошибка при получении оригинального анализа');
        }
      }

      throw new Error('Ошибка сети при получении оригинального анализа');
    }
  }

  /**
   * Код языка для API (ISO 639-3): ru -> rus, en -> eng.
   */
  private static languageToApiCode(lang: 'ru' | 'en'): string {
    return lang === 'ru' ? 'rus' : 'eng';
  }

  /**
   * Получает список публичных шерингов
   * @param limit - Максимальное количество записей (по умолчанию 12, максимум 12)
   * @param offset - Смещение для пагинации (по умолчанию 0)
   * @param language - Язык интерфейса ('ru' | 'en'): фильтр по language_code на бэкенде (rus/eng)
   * @returns Promise с данными публичных шерингов
   */
  static async getPublicShares(limit: number = 12, offset: number = 0, language?: 'ru' | 'en'): Promise<{
    data: any[];
    pagination: {
      limit: number;
      offset: number;
      total: number;
      has_more: boolean;
    };
  }> {
    const params: { limit: number; offset: number; language?: string } = { limit, offset };
    if (language) {
      params.language = this.languageToApiCode(language);
    }
    // Кэшируем только первый запрос (offset=0) на 30 секунд, с учетом limit и language
    const cacheKey = `limit:${limit}_lang:${language ?? 'all'}`;
    const cached = this.publicSharesCache.get(cacheKey);
    if (offset === 0 && cached &&
        Date.now() - cached.timestamp < 30000) {
      return cached.data;
    }

    try {
      const response = await axios.get(
        `${this.baseURL}/api/public-shares`,
        {
          params,
          timeout: 10000
        }
      );

      if (offset === 0) {
        this.publicSharesCache.set(cacheKey, {
          data: response.data,
          timestamp: Date.now()
        });
      }

      return response.data;
    } catch (error) {
      console.error('Error getting public shares:', error);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 400) {
          throw new Error(error.response.data.error || 'Некорректные параметры запроса');
        } else if (error.response?.status === 500) {
          throw new Error('Внутренняя ошибка сервера');
        } else {
          throw new Error(error.response?.data?.error || 'Ошибка при получении публичных шерингов');
        }
      }

      throw new Error('Ошибка сети при получении публичных шерингов');
    }
  }
} 